// src/pages/registrar-planeaciones.jsx 
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Dropzone from 'dropzone';
import 'dropzone/dist/dropzone.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../styles/formularios.css';
import '../styles/styles.css';
import logoInstituto from '../components/images/logo.png';
import logoSEP from '../components/images/SEP_Logo_2019.svg.png';
import { useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
// arriba de tu componente
import pdfIcon  from '../components/images/pdf.png';
import docIcon  from '../components/images/doc.png';
import xlsIcon  from '../components/images/xls.png';
import pptIcon  from '../components/images/ppt.png';
import txtIcon  from '../components/images/txt.png';
import { resolveApiBaseList } from '../utils/apiBase';

Dropzone.autoDiscover = false;

const RegistrarPlaneaciones = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const apiBases = useMemo(() => resolveApiBaseList(), []);

  // ---- estado del formulario ----
  const [idPlaneacion, setIdPlaneacion] = useState('');
  const [tema, setTema] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [idDocente, setIdDocente] = useState(null);

  // control envío para evitar doble clic
  const [submitting, setSubmitting] = useState(false);

  // ---- Dropzone ----
  const dropzoneRef = useRef(null);
  const dzInstanceRef = useRef(null);

  // Detecta ID del docente desde localStorage (ajusta si usas otra clave)
  useEffect(() => {
    const posiblesClaves = ['IdDocente', 'idDocente', 'docenteId', 'IdUsuarioDocente'];
    let found = null;
    for (const k of posiblesClaves) {
      const v = Number(localStorage.getItem(k));
      if (Number.isInteger(v) && v > 0) { found = v; break; }
    }
    setIdDocente(found);
  }, []);

  const fetchJsonWithFallback = useCallback(async (path, options = {}) => {
    let lastError = null;

    for (const base of apiBases) {
      const normalizedBase = base.replace(/\/$/, '');
      const normalizedPath = path.startsWith('/') ? path : `/${path}`;
      const url = `${normalizedBase}${normalizedPath}`;

      try {
        const response = await fetch(url, options);
        const text = await response.text();

        if (!response.ok) {
          let message = text || `Error ${response.status}`;
          try {
            const parsed = text ? JSON.parse(text) : null;
            if (parsed && typeof parsed === 'object') {
              message = parsed.message || parsed.error || message;
            }
          } catch (parseErr) {
            // mantiene mensaje original
          }
          const error = new Error(message);
          error.status = response.status;
          lastError = error;
          console.warn(`Warning: petición fallida a ${url}:`, error);
          continue;
        }

        const contentType = response.headers.get('content-type') || '';
        const trimmed = text.trim();
        if (!trimmed) {
          return { data: {}, baseUrl: normalizedBase };
        }

        if (contentType.includes('application/json') || trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        }

        try {
          return { data: JSON.parse(trimmed), baseUrl: normalizedBase };
        } catch (parseErr) {
          lastError = new Error(`Respuesta inválida desde ${url}`);
          console.warn(`Warning: contenido no JSON desde ${url} (content-type: ${contentType || 'desconocido'})`);
        }
      } catch (error) {
        lastError = error;
        console.warn(`Warning: error de red al solicitar ${url}:`, error);
      }
    }

    throw lastError || new Error('No se pudo contactar al servidor.');
  }, [apiBases]);

  // Obtiene el siguiente IdPlaneaciones (visual)
  const cargarSiguienteId = useCallback(async () => {
    try {
      const { data } = await fetchJsonWithFallback('/planeaciones/siguiente-id');
      setIdPlaneacion(data?.siguienteId ?? '');
    } catch (error) {
      console.warn('No se pudo obtener el siguiente ID de planeación:', error);
      setIdPlaneacion('');
    }
  }, [fetchJsonWithFallback]);

  useEffect(() => { cargarSiguienteId(); }, [cargarSiguienteId]);

  useEffect(() => {
    if (!dropzoneRef.current || Dropzone.instances.length > 0) return;

    const dz = new Dropzone(dropzoneRef.current, {
      url: '/fake-upload',           // no se usa; subimos con fetch
      autoProcessQueue: false,
      maxFilesize: 10,               // coherente con el texto visible (10 MB c/u)
      clickable: true,

      // Incluye MIME y extensiones (algunos navegadores reportan MIME raro)
      acceptedFiles: [
        'application/pdf', '.pdf',
        'application/msword', '.doc',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', '.docx',
        'application/vnd.ms-excel', '.xls',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', '.xlsx',
        'application/vnd.ms-powerpoint', '.ppt',
        'application/vnd.openxmlformats-officedocument.presentationml.presentation', '.pptx',
        'text/csv', '.csv',
        'text/plain', '.txt',
        'image/*', '.jpg', '.jpeg', '.png'
      ].join(','),

      // “Plan B”: fuerza aceptar si la extensión es .pdf aunque el MIME venga raro
      accept: (file, done) => {
        const name = file.name?.toLowerCase() || '';
        if (name.endsWith('.pdf')) return done();  // fuerza OK para PDF
        return done();
      },

      addRemoveLinks: true,
      dictDefaultMessage: 'Arrastra y suelta tus archivos aquí o haz clic para seleccionarlos.',
      init: function () {
        this.on('addedfile', function (file) {
          const ext = (file.name.split('.').pop() || '').toLowerCase();
          let icon = null;

          if (ext === 'pdf') icon = pdfIcon;
          else if (ext === 'doc' || ext === 'docx') icon = docIcon;
          else if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') icon = xlsIcon;
          else if (ext === 'ppt' || ext === 'pptx') icon = pptIcon;
          else if (ext === 'txt') icon = txtIcon;

          if (icon) {
            // Forzamos la miniatura con un asset local (si falla la red, igual funciona)
            this.emit('thumbnail', file, icon);
          }
        });

        this.on('error', (file, message) => {
          console.warn('Dropzone error:', message);
        });
      }
    });

    dzInstanceRef.current = dz; // guardar instancia para getAcceptedFiles/removeAllFiles

    return () => dz.destroy();
  }, []);

  // Aviso al salir si hay cambios o archivos pendientes
  useEffect(() => {
    const beforeUnload = (e) => {
      const dz = dzInstanceRef.current;
      const pending = dz?.getAcceptedFiles?.().length || 0;
      if (tema.trim() || descripcion.trim() || pending > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [tema, descripcion]);

  const handleSalir = () => {
    const rol = resolveRolBase(location.pathname);
    const dz = dzInstanceRef.current;
    const pending = dz?.getAcceptedFiles?.().length || 0;
    if (tema.trim() || descripcion.trim() || pending > 0) {
      Swal.fire({
        title: '¿Salir sin guardar?',
        text: 'Tienes cambios o archivos seleccionados. ¿Deseas salir de todos modos?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, salir',
        cancelButtonText: 'Cancelar'
      }).then(res => {
        if (res.isConfirmed) {
          if (rol) navigate(`/${rol}/inicio`, { replace: true });
          else navigate('/inicio', { replace: true });
        }
      });
      return;
    }

    if (rol) navigate(`/${rol}/inicio`, { replace: true });
    else navigate('/inicio', { replace: true });
  };

  // -------- enviar al backend --------
  const handleRegistrar = async () => {
    try {
      if (submitting) return;
      setSubmitting(true);

      // Verificación de instancia Dropzone
      const dz = dzInstanceRef.current;
      if (!dz || !dz.getAcceptedFiles) {
        Swal.fire('Error', 'El componente de subida no está listo.', 'error');
        return;
      }

      // Estado de red
      if (!navigator.onLine) {
        Swal.fire('Sin conexión', 'Revisa tu conexión a internet.', 'error');
        return;
      }

      // Tema requerido + longitudes
      if (!tema.trim()) {
        Swal.fire('Falta tema', 'Escribe el tema a impartir.', 'warning');
        return;
      }
      if (tema.trim().length < 5) {
        Swal.fire('Tema muy corto', 'Mínimo 5 caracteres.', 'info');
        return;
      }
      if (tema.length > 30) { // <-- tope real de BD
        Swal.fire('Tema muy largo', 'Máximo 30 caracteres (según BD).', 'info');
        return;
      }
      
      if (descripcion && descripcion.length > 254) { // <-- tope real de BD
        Swal.fire('Descripción muy larga', 'Máximo 254 caracteres (según BD).', 'info');
        return;
      }

      const files = dz.getAcceptedFiles() || [];
      if (files.length === 0) {
        Swal.fire('Sin archivos', 'Adjunta al menos un archivo.', 'info');
        return;
      }

      // Máximo número de archivos
      const MAX_FILES = 10;
      if (files.length > MAX_FILES) {
        Swal.fire('Demasiados archivos', `Máximo ${MAX_FILES} archivos.`, 'info');
        return;
      }

      // Validación fuerte de extensiones
      const allowedExt = ['pdf','doc','docx','xls','xlsx','csv','ppt','pptx','txt','jpg','jpeg','png'];
      const bad = files.filter(f => !allowedExt.includes((f.name.split('.').pop()||'').toLowerCase()));
      if (bad.length) {
        Swal.fire('Formato no permitido', `Archivo(s) no permitido(s): ${bad.map(b=>b.name).join(', ')}`, 'error');
        return;
      }

      // Duplicados por nombre
      const names = files.map(f=>f.name.toLowerCase());
      const dup = names.filter((n,i)=> names.indexOf(n)!==i);
      if (dup.length) {
        Swal.fire('Duplicados', `Hay nombres repetidos: ${[...new Set(dup)].join(', ')}`, 'warning');
        return;
      }

  // Nombres con caracteres problemáticos (Windows/Unix)
  // eslint-disable-next-line no-control-regex
  const forbidden = /[<>:"/\\|?*\u0000-\u001F]/g;
      const badNames = files.filter(f=> forbidden.test(f.name));
      if (badNames.length){
        Swal.fire('Nombre inválido', `Evita caracteres especiales en: ${badNames.map(b=>b.name).join(', ')}`, 'error');
        return;
      }

      // Tamaño total máximo
      const MAX_TOTAL_MB = 200; // ajusta si quieres
      const totalMB = files.reduce((sum,f)=> sum + (f.size||0)/1024/1024, 0);
      if (totalMB > MAX_TOTAL_MB) {
        Swal.fire('Límite superado', `El total (${totalMB.toFixed(1)} MB) excede ${MAX_TOTAL_MB} MB.`, 'warning');
        return;
      }

      // Límite por tipo (imágenes)
      const MAX_IMG_MB = 15;
      const bigImgs = files.filter(f=>{
        const ext = (f.name.split('.').pop()||'').toLowerCase();
        const isImg = ['jpg','jpeg','png'].includes(ext);
        return isImg && (f.size/1024/1024) > MAX_IMG_MB;
      });
      if (bigImgs.length){
        Swal.fire('Imágenes pesadas', `Reduce tamaño de: ${bigImgs.map(f=>f.name).join(', ')}`, 'info');
        return;
      }

      // Sanitización simple (además de lo que haga el backend)
      const sanitize = s => s.replace(/[<>]/g, '');
      const temaSafe = sanitize(tema.trim());
      const descSafe = sanitize(descripcion.trim());

      const fd = new FormData();

      if (idDocente) {
        fd.append('idDocente', idDocente);
      } else {
        const idUsuario = Number(localStorage.getItem('idUsuario'));
        if (Number.isInteger(idUsuario) && idUsuario > 0) {
          fd.append('idUsuario', idUsuario); // fallback si no hay idDocente
        } else {
          Swal.fire({
            icon: 'warning',
            title: 'Falta identificación',
            text: 'No se encontró idDocente ni idUsuario. Inicia sesión nuevamente.',
            confirmButtonText: 'Ir al inicio'
          }).then(()=> navigate('/inicio', {replace:true}));
          return;
        }
      }

      fd.append('tema', temaSafe);
      if (descSafe) fd.append('descripcion', descSafe);
      // Observaciones NO se envía (backend guarda NULL)
      // Status opcional (backend usa 'A')

      files.forEach(f => fd.append('archivos', f, f.name));

      const { data } = await fetchJsonWithFallback('/planeaciones/registrar', {
        method: 'POST',
        body: fd
      });

      Swal.fire({
        icon: 'success',
        title: 'Planeación registrada',
        text: `${data?.registros?.length || 1} archivo(s) subido(s) correctamente.`,
        timer: 2000,
        showConfirmButton: false
      });

      // Limpia formulario
      setTema('');
      setDescripcion('');
      dz.removeAllFiles(true);
      // Refresca ID sugerido
      cargarSiguienteId();
    } catch (e) {
      console.error(e);
      Swal.fire('Error', `No se pudo registrar la planeación.\n${e?.message || ''}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container py-4 px-4">
      {/* Encabezado */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <img src={logoInstituto} alt="Logo Instituto" className="logo" />
        <div className="flex-grow-1 text-center">
          <h4 className="mb-0">Instituto Silviano Carrillo</h4>
          <small className="text-muted fs-5">Registrar Planeaciones</small>
        </div>
        <img src={logoSEP} alt="Logo SEP" className="logo" />
      </div>

      {/* Formulario */}
      <div className="p-4 bg-light rounded shadow-sm">
        <form className="row g-3" id="formularioPlaneaciones" onSubmit={(e)=>e.preventDefault()}>
          <div className="col-md-4">
            <label htmlFor="idPlaneacion" className="form-label">ID Planeación</label>
            <input type="text" className="form-control" id="idPlaneacion"
                   value={idPlaneacion} readOnly />
          </div>

          <div className="col-md-8">
            <label htmlFor="temaImpartir" className="form-label">Tema a Impartir</label>
            <input
              type="text"
              className="form-control"
              id="temaImpartir"
              value={tema}
              onChange={(e)=>setTema(e.target.value)}
            />
          </div>

          <div className="col-12">
            <label htmlFor="descripcion" className="form-label">Descripción</label>
            <textarea
              className="form-control"
              id="descripcion"
              rows="5"
              placeholder="Describe los detalles de la planeación..."
              value={descripcion}
              onChange={(e)=>setDescripcion(e.target.value)}
            />
          </div>

          <div className="col-12">
            <label className="form-label">Subir Planeación (archivos, links, etc.)</label>
            <div id="dropzonePlaneaciones" className="dropzone" ref={dropzoneRef}></div>
            <div className="form-text mt-1">
              Archivos permitidos: PDF, DOC(X), XLS(X), PPT(X), CSV, TXT, imágenes. Máx 10 MB c/u.
            </div>
          </div>
        </form>
      </div>

      {/* Botones */}
      <div className="d-flex justify-content-center gap-3 mt-4">
        <button className="btn btn-success px-4" onClick={handleRegistrar} disabled={submitting}>
          {submitting ? 'Enviando…' : 'Registrar'}
        </button>
        <button type="button" className="btn btn-danger px-4" onClick={handleSalir}>
          Salir
        </button>
      </div>
    </div>
  );
};

/* ----------------- Helper para detectar el rol ----------------- */
function resolveRolBase(pathname) {
  // 1) Intenta desde la URL actual
  const roles = ['directora', 'coordinadora', 'docente', 'alumno'];
  const seg = pathname.split('/').filter(Boolean)[0];
  if (roles.includes(seg)) return seg;

  // 2) Respaldo desde localStorage.role (números mapeados a slugs)
  const roleId = Number(localStorage.getItem('role'));
  const map = { 1: 'directora', 2: 'coordinadora', 3: 'docente', 4: 'alumno' };
  return map[roleId] || null;
}

export default RegistrarPlaneaciones;
